<template>
    <b-card class="curator-image-editor-viewport">
        <b-row class="h-100">
            <b-col class="editor-column">
                <div class="bg-light m-1 p-2 border border-gray-500 sticky-top">
                    <asset-filter v-model="currentStatus" :assets="assetList"/>
                </div>

                <b-row cols-xl="3" cols="1" cols-lg="2" no-gutters align-content="stretch">
                    <b-col class="mb-2" v-for="asset in filteredAssets" :key="`${asset.id}-${asset.updated_at}`">
                        <div class="asset-square-item m-1 cursor-pointer" @click="changeCurrentAsset(asset)">
                            <div class="img-bg text-white text-shadow-sm asset-square-item-image position-relative"
                                 :style="backgroundImage(asset)">
                                <div @click.prevent.stop="handleAssetSelection(asset)"
                                     class="image-select-control">
                                    <b-form-checkbox v-model="assetSelection[asset.id]" @click.stop/>
                                </div>
                            </div>
                            <div class="pt-1 d-flex flex-column">
                                <div class="font-weight-semi-bold mb-2">{{ asset.name }}</div>
                                <b-badge pill :variant="statusVariant(asset.status)" class="badge-lg mb-2">
                                    <span class="font-weight-normal">{{ humanize(asset.status) }}</span>
                                    <i class="ml-2" :class="statusIcons[asset.status]"
                                       v-if="statusIcons[asset.status]"></i>
                                </b-badge>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </b-col>

            <b-col cols="auto" class="editor-column bg-light d-flex flex-column">
                <b-modal size="xl" v-model="showBulkModal" hide-footer lazy title="Bulk Asset Management">
                    <bulk-actions :selection="selectedAssets"
                                  :categories="categories"
                                  :submission-id="submissionId"
                                  source="submissions"
                                  @cancel="showBulkModal = false"
                    />
                </b-modal>

                <bulk-selection-bar :items="filteredAssets"
                                    v-model="assetSelection"
                                    class="py-2 sticky-top bg-light"
                                    :bulk-action-running="bulkActionRunning">

                    <template v-slot:menuSlot>
                        <b-button variant="primary" size="sm" @click="showBulkModal = true">
                            Bulk Actions
                        </b-button>
                    </template>

                </bulk-selection-bar>

                <asset-details v-model="currentAsset"
                               :url="url"
                               :categories="categories"
                               :tag-category-order="tagCategoryOrder"
                               class="flex-grow-1"
                               :release-form-url="releaseFormUrl"
                               :submission-release-forms="submissionReleaseForms"
                               @pendingChanges="pendingChanges = arguments[0]"
                               @change="handleAssetChange">

                </asset-details>
            </b-col>
        </b-row>
    </b-card>
</template>

<script>
import GeneralMixins from "../../../mixins/GeneralMixins";
import AssetDetails from "./AssetDetails";
import AssetStatus from "../../../models/AssetStatus";
import AssetFilter from "./AssetFilter";
import BulkSelectionBar from "./BulkSelectionBar";
import normalize from "json-api-normalize";
import Attributes from "../../../models/Attributes";
import BulkActions from "../AssetSearch/Bulk/BulkActions";

export default {
  components: {AssetFilter, AssetDetails, BulkSelectionBar, BulkActions},
  props: ['assets', 'url', 'tagStore', 'categories', 'submissionsUrl', 'tagCategoryOrder', 'releaseFormUrl', 'submissionReleaseForms', 'submissionId'],
  mixins: [GeneralMixins],

  data: function () {
    return {
      currentAsset: null,
      currentStatus: {all: true},
      assetList: this.assets || [],
      editing: {},
      assetSelection: {},
      bulkActionRunning: false,
      invalidAssetIds: null,
      pendingChanges: false,
      showBulkModal: false
    }
  },

  methods: {
    backgroundImage(asset) {
      return `background-image: url(${asset.thumb_url})`
    },

    statusVariant(status) {
      return AssetStatus.statusVariants[status] || 'dark'
    },

    handleAssetChange(asset) {
      const existingAsset = this.assetList.find(asset => asset.id === asset.id);
      if (existingAsset) {
        this.$set(this.assetList, this.assetList.indexOf(existingAsset), asset);
      }
    },

    changeCurrentAsset(asset) {
      if (this.pendingChanges) {
        if (confirm("You haven't saved your changes yet. Are you sure you want to change the asset?")) {
          this.currentAsset = asset;
        }
      } else {
        this.currentAsset = asset;
      }
    },

    handleAssetSelection(file) {
      this.$set(this.assetSelection, file.id, !this.assetSelection[file.id]);
    },

    handleBulkStatus: function (status) {
      const assetsToAlter = this.selectedAssets.filter(img => {
        return img.status !== status;
      });

      if (assetsToAlter.length === 0) {
        return this.showErrorToast(`All of the selected assets are already ${status}`);
      }

      if (!confirm(`This action will set the status to ${status} to all selected assets that don't have this status. Do you want to continue?`)) return;

      const attributes = {
        submission: {
          assets_attributes: assetsToAlter.map(img => {
            return {
              id: img.id,
              status: status
            }
          })
        }
      };

      this.bulkActionRunning = true;

      this.$http.patch(this.submissionsUrl, attributes).then(result => {
        this.invalidAssetIds = null;
        this.assetList = normalize(JSON.parse(result.body.assets)).get(Attributes.assetWithIncludesAttributes);
        this.assetSelection = {};
        this.showToast("Selected assets were updated successfully", "Success")
      }, rejectionResult => {
        this.invalidAssetIds = rejectionResult.body.invalid_asset_ids;
        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(result => {
        this.bulkActionRunning = false
      });
    }
  },

  computed: {
    selectedStatuses() {
      return Object.keys(this.currentStatus).filter(key => this.currentStatus[key]);
    },

    filteredAssets() {
      if (this.currentStatus['all']) {
        return this.assetList;
      }
      return this.assetList.filter(asset => this.selectedStatuses.includes(asset.status))
    },

    statusIcons() {
      return AssetStatus.statusIcons;
    },

    selectedAssets: function () {
      const assetIds = Object.keys(this.assetSelection).filter(assetId => this.assetSelection[assetId] === true);
      if (assetIds.length > 0) {
        return assetIds.map(assetId => {
          return this.filteredAssets.find(asset => asset.id === assetId)
        })
      }
      return [];
    }
  }
}
</script>